import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useHistory, useLocation, useParams } from 'react-router-dom'
import { failureText, lmsAdmin, readFailureText } from '../api'
import { connectionStatus, logoFor, ORIGIN_EXTRA_PROBLEM, originExtra, originOf } from '../format'
import { dateTimeText, utcText } from '../../../shared/date/local-time'
import { useDialogFocus } from '../../../shared/organization-deletion/dialog-focus'
import { FleetPage, Notice } from '../../../shared/sso-workspace/fleet-page'
import { LeaveGuard } from '../../../shared/sso-workspace/leave-guard'
import { DockedSaveBar } from '../../../shared/sso-workspace/save-bar'
import { Pill } from '../../../shared/sso-workspace/pill'
import { useRoleOrg } from '../../../role/hook/role-org'
import { ROLE_ORG } from '../../../role/role.context'
import { SigningKeySection } from './signing-key-section'
import { LtiRegistrationSection, useLtiRegistration } from './lti-registration-section'
import { LtiToolKeySection } from './lti-tool-key-section'
import { LtiDiagnostics } from './lti-diagnostics'
import { FailedStatementsSection } from './failed-statements'
import { ConnectionKeysPanel } from './connection-keys-panel'
import { LaunchSettings, StrictSignInSwitch, strictSignInDefault, useLaunchSettings } from './connection-launch-settings'
import { PackagesToRebuild } from './packages-to-rebuild'
import '../lms-admin.scss'
import { LmsIcon } from '../lms-icon'
import { LmsTrail, lmsTrail } from '../lms-trail'

// Connection Drawer (docs/08 §4, design edxr-connection-drawer-v1.0.html). It
// began as the design's drawer; it is a standard admin page now (owner feedback
// 2026-10-20: a card that used part of the page, with an X, did not follow the
// rest of the admin): FleetPage's pane with the title, a back link to the
// Connections list, the sections full width as cards, and Save and Discard in
// the admin's docked save bar while something is unsaved (save-bar.js; owner
// 2026-10-23: a card of buttons floating over the form was not docked). Its
// History is the page chrome's, from the record route (record-routes.js), as on
// every record's page.
// Artifact states implemented: new-picker (no platform yet), new-filled
// (platform picked, defaults applied), edit existing, invalid (error
// banner + field errors). Each connection signs its packages with its own
// key (plan Task 14C): the edit view's Package key section shows it and
// rotates or revokes it. Under it, the API keys bound to the connection
// (Phase D D7, connection-keys-panel.js: absent while the staff-only switch
// is closed, and on an LTI 1.3 connection, which has no package and uses no
// bound key). The referrer choice is stored in
// config_overrides.security.referrer_check for forward compatibility
// (deviations D-042). A cmi5 connection also accepts a launch only when the
// home page its actor names is the connection's own LMS address or one the
// connection allows (Phase C prep, plan Task 4): the Security section edits
// that list, and the Refused cmi5 home pages section lists the home pages the
// service refused, each with an Allow button. Anyone holding a package
// credential can make a home page appear there, another connection's own
// address included: that row is marked, and its Allow asks first. A cmi5 or
// xapi-lrs connection sends xAPI statements: while it is paused, or the
// organization's LMS is off, they are held, and the Held xAPI statements
// section says how many and how long they are kept (Phase C P4, F7). An edit
// also names the connection's system key (Phase F E1b): the partner's id space
// its SCORM learner ids are looked up in, empty for the LMS's own; the service
// refuses a change while the connection has linked learners. Its launch
// settings (connection-launch-settings.js, Phase F Task 7) sit under Security:
// a sign-in for every launch, the origins a browser may launch from, the
// anomaly block, who can connect an account, and how far the connection can
// tell who is launching. Its diagnostics also list the courses whose package
// was built before the automatic launch (Phase F Task 8), each with Rebuild.
//
// How courses open (the connection's delivery) is chosen when the connection is
// made and fixed after: a SCORM package (the default), or LTI 1.3 (Phase L
// Task 8), where EdXR is a tool in the LMS. An LTI 1.3 connection has no
// package, no SCO and no partner system key, so the page has none of the
// SCORM-only controls (the referrer check, the allowed origins, the system key,
// the package key, the packages to rebuild). It has, in their place, the tool's
// addresses and the settings to choose in the LMS (lti-registration-section.js,
// there from the day it is made), the details the LMS gives back, the tool key
// (lti-tool-key-section.js) and the refused launches (lti-diagnostics.js). An
// archived LTI 1.3 connection launches nobody and its registration is gone, so
// its whole page is read-only (no Save). So is an archived connection's of any
// delivery (the service refuses every write to one, 409 CONNECTION_ARCHIVED), and
// a page opened by a role that may only read the LMS (Pause, Resume and Save need
// the role's update access). Leaving the page with anything typed and not saved
// (the sidebar, Back, Cancel, a reload or a closed tab) asks first, as the other
// admin pages do (the shared LeaveGuard).

// sameList: two allowed lists hold the same origins in the same order.
const sameList = (a, b) => a.length === b.length && a.every((origin, i) => origin === b[i])

// homePageFieldErrors is the service's word on an allowed list it refused (a
// 400's details.fields): the list's own, and each entry's by its index in the
// list sent, keyed here by the entry.
const homePageFieldErrors = (e, sent) => {
  const fields = (e && e.details && e.details.fields) || {}
  const entries = {}
  ;(sent || []).forEach((origin, i) => {
    const message = fields[`cmi5_allowed_home_pages[${i}]`]
    if (message) entries[origin] = message
  })
  return { list: fields.cmi5_allowed_home_pages || null, entries }
}

const NO_HOME_PAGE_ERRORS = { list: null, entries: {} }

// The Connections list: where the back link, Cancel and a saved edit go.
const CONNECTIONS_PATH = '/org/lms/connections'

const omitLtiPlatform = ({ ltiPlatform: _, ...rest }) => rest

// allowErrorMessage is what a refused Allow says: the service's word on the
// list, or on an entry (named), else the service's own sentence, else (a
// proxy's bare 502, a network failure) the page's own words.
const allowErrorMessage = (e, sent) => {
  const { list, entries } = homePageFieldErrors(e, sent)
  const named = Object.entries(entries).map(([origin, message]) => `${origin}: ${message}`)
  return list || named.join(' ') || failureText(e, "The address couldn't be allowed.")
}

// What allowing another connection's own address does.
const otherConnectionWarning = other =>
  `This is the address of your connection "${other.name}". Allowing it lets this connection's launches sign in that connection's linked learners.`

// The xAPI statements a connection holds while it is paused or the
// organization's LMS is off (the diagnostics' held_statements, Phase C P4,
// F7): sent once it is back on, kept max_age_days, then dropped, never sent.
// `expired` counts those dropped in the last 30 days. Drawn only when there
// is something to say; `error` is a failed diagnostics read the page shows
// nowhere else.
const HeldStatementsSection = ({ held, error }) => {
  const holding = !!held && held.held > 0
  const dropped = !!held && held.expired > 0
  if (!error && !holding && !dropped) return null
  return (
    <div className="form-section" data-testid="held-statements">
      <div className="form-section-title">
        <span className="num">
          <LmsIcon name="clock" regular style={{ fontSize: 9 }} />
        </span>
        <h2 className="label">Held xAPI statements</h2>
      </div>
      <div className="field">
        {error ? (
          <div className="field-help">{readFailureText('The held statements', error)}</div>
        ) : (
          <React.Fragment>
            {holding && (
              <React.Fragment>
                <div className="field-help" style={{ marginTop: 0 }}>
                  {held.held} xAPI {held.held === 1 ? 'statement is' : 'statements are'} waiting to be sent: this connection is
                  paused, or LMS is turned off for the organization. They are sent once it is back on.
                </div>
                {held.oldest_held_at && (
                  <div className="field-help" style={{ marginTop: 0 }} data-testid="held-statements-oldest">
                    {'Oldest held since '}
                    <span title={utcText(held.oldest_held_at)}>{dateTimeText(held.oldest_held_at)}</span>
                  </div>
                )}
                <div className="field-help" style={{ marginTop: 0 }}>
                  Held statements are kept for {held.max_age_days} days, then dropped without being sent.
                </div>
              </React.Fragment>
            )}
            {dropped && (
              <div
                className="field-error"
                style={{ color: 'var(--edxr-status-warning-text)' }}
                data-testid="held-statements-expired"
              >
                <LmsIcon name="triangle-exclamation" />
                <span>
                  {held.expired} {held.expired === 1 ? 'statement was' : 'statements were'} dropped in the last 30 days, never
                  sent: {held.expired === 1 ? 'it was' : 'they were'} held longer than {held.max_age_days} days.
                </span>
              </div>
            )}
          </React.Fragment>
        )}
      </div>
    </div>
  )
}

// The create form's sign-in switch is a SCORM connection's (it is in the
// SCORM security section) and starts as SCORM's default, on. An LTI 1.3
// connection is made without it (cmi5 and xAPI record-store connections are
// made elsewhere).
const NEW_CONNECTION_DELIVERY = 'scorm'

// The referrer check a form starts with, and the one a connection shows (the
// page does not read the stored one; every save sends this page's choice).
const DEFAULT_REFERRER = 'strict'

const NO_DISPLAY = { footer: '', email: '', url: '' }

// A section that cannot be used yet (inert keeps the keyboard out of it too).
const DIMMED = { opacity: 0.5, pointerEvents: 'none' }

const REFERRER_OPTIONS = [
  ['strict', 'Strict', 'Only accept SCORM launches from your LMS host. Recommended.'],
  ['permissive', 'Permissive', 'Accept any subdomain of your LMS domain. Use if your LMS proxies through multiple hosts.'],
  ['disabled', 'Disabled', 'Skip referrer validation entirely. Only safe in isolated environments.']
]

// How a connection's courses open, in words. The first two are the choice a new
// connection offers; every delivery is named, read-only, on an existing one.
const DELIVERY_OPTIONS = [
  ['scorm', 'SCORM package', 'EducationXR builds a launcher package for each course, and you upload it to your LMS.'],
  ['lti13', 'LTI 1.3', 'Your LMS adds EducationXR as an external tool. Nothing to upload: your LMS signs who is launching, and learners open courses from its own activities.']
]

const DELIVERY_NAME = {
  scorm: 'SCORM package',
  cmi5: 'cmi5',
  'xapi-lrs': 'xAPI record store',
  lti13: 'LTI 1.3'
}

// The platform a Quick start tile chose: the Connections page's zero state opens
// /org/lms/connections/new?preset=<id>. Only a plain id counts; the page still
// checks it is a published preset before choosing it.
const presetFromAddress = search => {
  const wanted = new URLSearchParams(search).get('preset')
  return /^[1-9]\d{0,9}$/.test(wanted || '') ? Number(wanted) : 0
}

export const ConnectionDrawer = ({ mode }) => {
  const history = useHistory()
  const { search } = useLocation()
  const { connectionId } = useParams()

  const [presets, updatePresets] = useState(null)
  const [presetID, updatePresetID] = useState(0)
  // The platform the address chose (a new connection only): the person did not
  // pick it, so it is not an unsaved change.
  const [addressPresetID, updateAddressPresetID] = useState(0)
  const [pickerSearch, updatePickerSearch] = useState('')
  const [name, updateName] = useState('')
  const [baseURL, updateBaseURL] = useState('')
  // The partner system key the connection reads its SCORM learner ids in
  // (edit only; '' reads the LMS's own space): as edited, as stored, and the
  // service's word on it when it refused it.
  const [systemKey, updateSystemKey] = useState('')
  const [savedSystemKey, updateSavedSystemKey] = useState('')
  const [systemKeyError, updateSystemKeyError] = useState(null)
  const [referrer, updateReferrer] = useState(DEFAULT_REFERRER)
  const [overrideFooterMode, updateOverrideFooterMode] = useState('') // '' = inherit org setting
  const [overrideSupportEmail, updateOverrideSupportEmail] = useState('')
  const [overrideSupportURL, updateOverrideSupportURL] = useState('')
  // The display overrides as stored, to tell whether the form changed them.
  const [savedDisplay, updateSavedDisplay] = useState(NO_DISPLAY)
  const [status, updateStatus] = useState('active')
  // The name as stored, which names the connection in the status control (the
  // name field changes as it is typed), and the status change in flight: 'pause'
  // or 'resume' while its call runs, and what a refused one said.
  const [savedName, updateSavedName] = useState('')
  const [changingStatus, updateChangingStatus] = useState(null)
  const [statusError, updateStatusError] = useState(null)
  // What a screen reader hears after a change (the focused button's name changes
  // from Pause to Resume, which it does not announce).
  const [statusNote, updateStatusNote] = useState('')
  const [error, updateError] = useState(null)
  const [fieldErrors, updateFieldErrors] = useState({})
  const [busy, updateBusy] = useState(false)
  // The address as saved: a different origin than the one typed asks whether
  // it is the same LMS before anything is sent.
  const [savedBaseURL, updateSavedBaseURL] = useState('')
  const [askingOrigin, updateAskingOrigin] = useState(false)
  // The connection's package signing key (edit only; plan Task 14C).
  const [signingKey, updateSigningKey] = useState(null)
  // Whether the connection has been read: its API keys panel is the loaded
  // connection's, never mounted before the read or after a failed one.
  const [loaded, updateLoaded] = useState(false)
  // How the connection delivers its courses (scorm, cmi5 or xapi-lrs). A cmi5
  // one checks the home page each launch names (Phase C prep, plan Task 4).
  const [delivery, updateDelivery] = useState('')
  // A new connection's choice: scorm (the default) or lti13.
  const [deliveryChoice, updateDeliveryChoice] = useState('scorm')
  // The origins, besides the connection's own, a cmi5 launch's home page may
  // name: as edited and as stored. A save sends the list only when it changed.
  const [allowedHomePages, updateAllowedHomePages] = useState([])
  const [savedAllowedHomePages, updateSavedAllowedHomePages] = useState([])
  const [homePageDraft, updateHomePageDraft] = useState('')
  const [homePageError, updateHomePageError] = useState(null)
  const [homePageServerErrors, updateHomePageServerErrors] = useState(NO_HOME_PAGE_ERRORS)
  // The connection's diagnostics: the cmi5 home pages the service refused
  // (null while they load), the xAPI statements it holds, and a failed read.
  // Then the home page being allowed.
  const [refusedHomePages, updateRefusedHomePages] = useState(null)
  const [heldStatements, updateHeldStatements] = useState(null)
  const [packagesToRebuild, updatePackagesToRebuild] = useState([])
  const [failedStatements, updateFailedStatements] = useState(null)
  // An LTI 1.3 connection's: why launches were refused and the grade returns
  // that failed (both null while they load), and how many grades the LMS
  // answered "already applied" (a 409) to.
  const [ltiRefusals, updateLtiRefusals] = useState(null)
  const [ltiScoreFailures, updateLtiScoreFailures] = useState(null)
  const [ltiScoreApplied409, updateLtiScoreApplied409] = useState(0)
  const [diagnosticsError, updateDiagnosticsError] = useState(null)
  const [allowing, updateAllowing] = useState(null)
  const [allowError, updateAllowError] = useState(null)
  // A home page that is another connection's address, while the question
  // whether to allow it waits for an answer: { entry, via } where via is
  // 'allow' (its row's Allow) or 'add' (typed into the list).
  const [confirmingAllow, updateConfirmingAllow] = useState(null)
  const allowDialog = useRef(null)
  const allowCancel = useRef(null)
  useDialogFocus(confirmingAllow !== null, allowDialog, allowCancel)
  // Set while an Allow runs, before the page redraws: a second press (a double
  // click) starts no second write.
  const allowRunning = useRef(false)
  // The same for a status change.
  const statusRunning = useRef(false)

  const launch = useLaunchSettings()
  // The create form's sign-in for every launch (an edit's is launch's).
  const [newStrict, updateNewStrict] = useState(strictSignInDefault(NEW_CONNECTION_DELIVERY))

  const isEdit = mode === 'edit'
  const isCmi5 = isEdit && delivery === 'cmi5'
  const isXapiLrs = isEdit && delivery === 'xapi-lrs'
  // An LTI 1.3 connection (Phase L): the one being edited, or the one being made.
  const isLtiEdit = isEdit && delivery === 'lti13'
  const isLti = isEdit ? isLtiEdit : deliveryChoice === 'lti13'
  // The SCORM-only controls (the referrer check, the system key, the package
  // key): not drawn for an LTI 1.3 connection, nor before an existing
  // connection has been read and is known not to be one.
  const scormOnly = !isLti && (!isEdit || loaded)
  // Pause, Resume and Save need the LMS role's update access (the service answers
  // 403 without it); a sys admin passes (useRoleOrg).
  const lms = useRoleOrg(ROLE_ORG.LMS)
  const canChange = !!lms.accessUpdate
  // The service refuses every write to an archived connection, of any delivery
  // (409 CONNECTION_ARCHIVED), so nothing on its page can change; nor can a role
  // that may only read.
  const archived = isEdit && status === 'archived'
  const readOnly = isEdit && (archived || !canChange)
  // Before a platform is picked, the sections after it are out of reach.
  const blocked = !isEdit && presetID === 0

  // Its registration, read from its own route once the connection is known to
  // be one (the tool's addresses, the LMS's details, the tool key).
  const lti = useLtiRegistration(connectionId, isLtiEdit && loaded)
  // The refusal to drop unsaved platform details goes once there are none (saved
  // or discarded), so the banner never points at nothing.
  useEffect(() => {
    if (!lti.dirty) updateFieldErrors(previous => (previous.ltiPlatform ? omitLtiPlatform(previous) : previous))
  }, [lti.dirty])

  const loadDiagnostics = () =>
    lmsAdmin
      .getConnectionDiagnostics(connectionId)
      .then(result => {
        updateDiagnosticsError(null)
        updateRefusedHomePages((result && result.refused_home_pages) || [])
        updateHeldStatements((result && result.held_statements) || null)
        updatePackagesToRebuild((result && result.packages_to_rebuild) || [])
        updateFailedStatements((result && result.failed_statements) || null)
        updateLtiRefusals((result && result.lti_refusals) || [])
        // The service always sends this list ([] when none failed). An answer without
        // it (a service older than the grade passback) is read as none failed, so
        // the section never waits for a list that will not come.
        updateLtiScoreFailures(result && Array.isArray(result.lti_score_failures) ? result.lti_score_failures : [])
        updateLtiScoreApplied409(result && typeof result.lti_score_applied_409 === 'number' ? result.lti_score_applied_409 : 0)
      })
      .catch(updateDiagnosticsError)

  useEffect(() => {
    // The published presets: an organization route, so an owner (who holds no
    // system scope) sees the platforms too. It returns published ones only.
    lmsAdmin
      .listPublishedPresets()
      .then(result => {
        const published = result.presets || []
        updatePresets(published)
        const wanted = isEdit ? 0 : presetFromAddress(search)
        if (wanted && published.some(p => p.id === wanted)) {
          updatePresetID(wanted)
          updateAddressPresetID(wanted)
        }
      })
      .catch(() => updatePresets([]))
    if (isEdit && connectionId) {
      updateLoaded(false)
      lmsAdmin
        .getConnection(connectionId)
        .then(connection => {
          updateName(connection.name)
          updateSavedName(connection.name)
          updateBaseURL(connection.lms_base_url)
          updateSavedBaseURL(connection.lms_base_url)
          updateSystemKey(connection.system_key || '')
          updateSavedSystemKey(connection.system_key || '')
          updatePresetID(connection.preset ? connection.preset.id : 0)
          updateStatus(connection.status)
          updateSigningKey(connection.signing_key || null)
          updateDelivery(connection.delivery || '')
          updateAllowedHomePages(connection.cmi5_allowed_home_pages || [])
          updateSavedAllowedHomePages(connection.cmi5_allowed_home_pages || [])
          launch.load(connection)
          // The diagnostics of every connection: the packages to rebuild are any
          // connection's; the refused home pages and held statements are the
          // ones that send xAPI statements' (cmi5, xapi-lrs).
          loadDiagnostics()
          const display = (connection.config_overrides && connection.config_overrides.display) || {}
          updateOverrideFooterMode(display.footer_mode || '')
          updateOverrideSupportEmail(display.support_email || '')
          updateOverrideSupportURL(display.support_url || '')
          updateSavedDisplay({ footer: display.footer_mode || '', email: display.support_email || '', url: display.support_url || '' })
          updateLoaded(true)
        })
        .catch(updateError)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectionId, isEdit])

  const selectedPreset = useMemo(
    () => (presets || []).find(p => p.id === presetID),
    [presets, presetID]
  )

  const validate = () => {
    const errors = {}
    if (!name.trim()) errors.name = 'A display name is required.'
    // An unchanged address is not re-checked on an edit: one saved before the
    // rules must not block a rename (Task 14A carry).
    const addressChanged = !isEdit || baseURL.trim() !== savedBaseURL.trim()
    if (addressChanged && !/^https?:\/\/.+/.test(baseURL.trim())) errors.baseURL = 'Enter the full LMS base URL, including the scheme (https://…).'
    if (!isEdit && presetID === 0) errors.preset = 'Pick a platform first.'
    // An address typed but not added would be lost without a word.
    if (homePageDraft.trim()) errors.allowedHomePages = 'Press Add to allow this address, or clear it.'
    if (isEdit && !launch.validate()) errors.launchOrigins = 'Press Add to allow this address, or clear it.'
    // Platform details typed and not saved would be dropped without a word:
    // they are saved by their own button.
    if (isLtiEdit && lti.dirty) errors.ltiPlatform = 'Save the platform details first, or discard them.'
    updateFieldErrors(errors)
    return Object.keys(errors).length === 0
  }

  // config_overrides.display carries only explicitly-set fields — the
  // strict server whitelist rejects anything else (spec §4). Empty = inherit.
  const displayOverrides = () => {
    const display = {}
    if (overrideFooterMode) display.footer_mode = overrideFooterMode
    if (overrideSupportEmail.trim()) display.support_email = overrideSupportEmail.trim()
    if (overrideSupportURL.trim()) display.support_url = overrideSupportURL.trim()
    return Object.keys(display).length ? { display } : {}
  }

  // save sends the form. issuerChange is the admin's answer when the origin
  // of the address changed ('moved' or 'new_lms'); it is sent only then.
  const save = async issuerChange => {
    updateBusy(true)
    updateError(null)
    updateHomePageServerErrors(NO_HOME_PAGE_ERRORS)
    updateSystemKeyError(null)
    launch.clearErrors()
    // The allowed list only when it changed (the service leaves it as stored).
    const sentHomePages = isEdit && !sameList(allowedHomePages, savedAllowedHomePages) ? allowedHomePages : null
    // The launch settings the connection carries, only those that changed.
    const launchChanges = isEdit ? launch.changes() : {}
    try {
      if (isEdit) {
        await lmsAdmin.patchConnection(connectionId, {
          name: name.trim(),
          // The address only when it changed: resending an address saved
          // before the rules would block a plain rename (Task 14A carry).
          ...(baseURL.trim() !== savedBaseURL.trim() ? { lms_base_url: baseURL.trim() } : {}),
          ...(issuerChange ? { issuer_change: issuerChange } : {}),
          ...(sentHomePages ? { cmi5_allowed_home_pages: sentHomePages } : {}),
          // The system key only when it changed; an emptied one clears it.
          ...(systemKey.trim() !== savedSystemKey ? { system_key: systemKey.trim() } : {}),
          ...launchChanges,
          // an LTI 1.3 connection has no SCORM referrer check to send
          config_overrides: isLti ? displayOverrides() : { security: { referrer_check: referrer }, ...displayOverrides() }
        })
        history.push(CONNECTIONS_PATH)
      } else {
        const created = await lmsAdmin.createConnection({
          name: name.trim(),
          preset_id: presetID,
          lms_base_url: baseURL.trim(),
          // A SCORM connection's sign-in for every launch is what its switch shows;
          // an LTI 1.3 one is made without it and starts off, the service's default
          // for its delivery (rapid-api-lms D-133); its drawer has the switch.
          ...(isLti ? { delivery: 'lti13' } : { strict_sign_in: newStrict }),
          config_overrides: isLti ? displayOverrides() : { security: { referrer_check: referrer }, ...displayOverrides() }
        })
        history.push(`/org/lms/connections/${created.id}`)
      }
    } catch (e) {
      updateBusy(false)
      // The server is the authority on whether the origin changed. If it says
      // the address needs an answer the browser did not see (the two read an
      // address slightly differently), ask the same question and resubmit,
      // rather than leaving only an error banner. An answer already sent that
      // is refused again is a real error.
      if (isEdit && !issuerChange && e && e.code === 'ISSUER_CHANGE_REQUIRED') {
        updateAskingOrigin(true)
        return
      }
      updateHomePageServerErrors(homePageFieldErrors(e, sentHomePages))
      launch.refused(e, launchChanges.allowed_origins)
      updateSystemKeyError((e && e.details && e.details.fields && e.details.fields.system_key) || null)
      updateError(e)
    }
  }

  // addHomePage adds the typed origin to the allowed list in the form the
  // service stores (lowercase, no default port). An address with more than
  // its origin is refused, saying what to take out, never trimmed to it (CP2
  // review R1): the service refuses it too. One the refused list names as
  // another connection's address is added only once the admin has said so.
  const addHomePage = () => {
    const origin = originOf(homePageDraft)
    const extra = origin === null ? null : originExtra(homePageDraft)
    let problem = null
    if (origin === null) problem = 'Enter a web address, such as https://lms.example.com.'
    else if (extra) problem = ORIGIN_EXTRA_PROBLEM[extra]
    else if (origin === originOf(baseURL)) problem = "That is this connection's own LMS address, which is always allowed."
    else if (allowedHomePages.includes(origin)) problem = 'Already in the list.'
    updateHomePageError(problem)
    if (problem) return
    const flagged = (refusedHomePages || []).find(entry => entry.home_page === origin && entry.other_connection)
    if (flagged) {
      updateConfirmingAllow({ entry: flagged, via: 'add' })
      return
    }
    addToList(origin)
  }

  const addToList = origin => {
    updateAllowedHomePages(prev => (prev.includes(origin) ? prev : [...prev, origin]))
    updateHomePageDraft('')
    updateFieldErrors(({ allowedHomePages: _, ...rest }) => rest)
  }

  // The referrer check is a radio group: a click, Enter or Space chooses (not on a
  // page that cannot change).
  const chooseReferrer = key => {
    if (!readOnly) updateReferrer(key)
  }

  const removeHomePage = origin => updateAllowedHomePages(allowedHomePages.filter(entry => entry !== origin))

  // allowHomePage reads the list as stored now (another admin may have changed
  // it since this page opened), stores it with a refused home page added, then
  // reads the refused home pages again. An unsaved edit of the list is not
  // saved by it: that edit stays pending, with the origin added. With none
  // pending, the list shown is the one stored. Never two at once, and never
  // during a save.
  const allowHomePage = async origin => {
    if (allowRunning.current || busy) return
    allowRunning.current = true
    updateAllowing(origin)
    updateAllowError(null)
    const savedBefore = savedAllowedHomePages
    let sent = null
    try {
      const current = await lmsAdmin.getConnection(connectionId)
      const list = (current && current.cmi5_allowed_home_pages) || []
      sent = list.includes(origin) ? list : [...list, origin]
      const updated = await lmsAdmin.patchConnection(connectionId, { cmi5_allowed_home_pages: sent })
      const stored = (updated && updated.cmi5_allowed_home_pages) || sent
      updateAllowedHomePages(prev =>
        sameList(prev, savedBefore) ? stored : prev.includes(origin) ? prev : [...prev, origin]
      )
      updateSavedAllowedHomePages(stored)
      await loadDiagnostics()
    } catch (e) {
      updateAllowError(allowErrorMessage(e, sent))
    } finally {
      allowRunning.current = false
      updateAllowing(null)
    }
  }

  // Another connection's own address is allowed only once the admin has said
  // so: this connection's launches would then sign in that one's learners.
  const askAllow = entry => (entry.other_connection ? updateConfirmingAllow({ entry, via: 'allow' }) : allowHomePage(entry.home_page))

  const confirmAllow = async () => {
    const { entry, via } = confirmingAllow
    updateConfirmingAllow(null)
    if (via === 'add') addToList(entry.home_page)
    else await allowHomePage(entry.home_page)
  }

  const originChanged = () => {
    const before = originOf(savedBaseURL)
    const after = originOf(baseURL)
    // An LTI 1.3 connection's learners follow its registration's issuer, never
    // its address, so a new address moves no link and asks nothing.
    return isEdit && !isLti && before !== null && after !== null && before !== after
  }

  const handleSave = async () => {
    if (readOnly || !validate() || busy || allowRunning.current) return
    if (originChanged()) {
      updateAskingOrigin(true)
      return
    }
    await save('')
  }

  const answerOrigin = async issuerChange => {
    updateAskingOrigin(false)
    await save(issuerChange)
  }

  // What the form holds that is not saved, one entry per change: each field of
  // the page, each launch setting, the allowed lists (and an address typed into
  // one and not added) and the LTI platform details. A new connection is
  // compared with an empty form. Nothing is unsaved on a page that cannot
  // change. The save bar counts them; the leave guard asks while there is one.
  const displayChanges = [
    overrideFooterMode !== savedDisplay.footer,
    overrideSupportEmail.trim() !== savedDisplay.email.trim(),
    overrideSupportURL.trim() !== savedDisplay.url.trim()
  ]
  const unsaved = readOnly
    ? []
    : isEdit
      ? [
          name.trim() !== savedName.trim(),
          baseURL.trim() !== savedBaseURL.trim(),
          systemKey.trim() !== savedSystemKey.trim(),
          referrer !== DEFAULT_REFERRER,
          ...displayChanges,
          !sameList(allowedHomePages, savedAllowedHomePages),
          homePageDraft.trim() !== '',
          ...Object.keys(launch.changes()).map(() => true),
          launch.draft.trim() !== '',
          isLtiEdit && lti.dirty
        ]
      : [
          presetID > 0 && presetID !== addressPresetID,
          name.trim() !== '',
          baseURL.trim() !== '',
          referrer !== DEFAULT_REFERRER,
          ...displayChanges,
          deliveryChoice !== NEW_CONNECTION_DELIVERY,
          newStrict !== strictSignInDefault(NEW_CONNECTION_DELIVERY)
        ]
  const unsavedCount = unsaved.filter(Boolean).length
  const dirty = unsavedCount > 0

  // The save bar's Discard: the form as stored (a new connection's, empty, with
  // the platform the address chose), nothing typed into a list, no errors. The
  // platform details of an LTI 1.3 connection go back too.
  const discard = () => {
    if (busy) return
    updateError(null)
    updateFieldErrors({})
    updateHomePageError(null)
    updateHomePageServerErrors(NO_HOME_PAGE_ERRORS)
    updateSystemKeyError(null)
    updateHomePageDraft('')
    updateReferrer(DEFAULT_REFERRER)
    updateOverrideFooterMode(savedDisplay.footer)
    updateOverrideSupportEmail(savedDisplay.email)
    updateOverrideSupportURL(savedDisplay.url)
    if (isEdit) {
      updateName(savedName)
      updateBaseURL(savedBaseURL)
      updateSystemKey(savedSystemKey)
      updateAllowedHomePages(savedAllowedHomePages)
      launch.reset()
      if (isLtiEdit && lti.dirty) lti.discard()
    } else {
      updatePresetID(addressPresetID)
      updatePickerSearch('')
      updateName('')
      updateBaseURL('')
      updateDeliveryChoice(NEW_CONNECTION_DELIVERY)
      updateNewStrict(strictSignInDefault(NEW_CONNECTION_DELIVERY))
    }
  }

  // The Connections list's row offers Pause on an active connection and Resume
  // on a paused one (an archived one neither), with these two calls and no
  // question first; the page offers the same, to a role that may change it. It
  // changes the status only: what is typed in the form is neither sent nor lost.
  const statusVerb = status === 'active' ? 'pause' : status === 'paused' ? 'resume' : null

  // The status as the service has it now: another admin may have archived,
  // paused or resumed the connection since this page loaded, which is usually why
  // a change was refused. A read that fails leaves the status as it was.
  const rereadStatus = async () => {
    try {
      const current = await lmsAdmin.getConnection(connectionId)
      if (current && current.status) updateStatus(current.status)
    } catch {
      // the refusal already shown is the answer
    }
  }

  const changeStatus = async () => {
    if (statusRunning.current || !statusVerb || !canChange) return
    const verb = statusVerb
    statusRunning.current = true
    updateChangingStatus(verb)
    updateStatusError(null)
    updateStatusNote('')
    try {
      if (verb === 'pause') await lmsAdmin.pauseConnection(connectionId)
      else await lmsAdmin.resumeConnection(connectionId)
      updateStatus(verb === 'pause' ? 'paused' : 'active')
      updateStatusNote(`${savedName} is ${verb === 'pause' ? 'paused' : 'active'}.`)
    } catch (e) {
      updateStatusError(e && e.readable ? `Couldn't ${verb} ${savedName}: ${e.message}` : `Couldn't ${verb} ${savedName}. Try again in a moment.`)
      await rereadStatus()
    } finally {
      statusRunning.current = false
      updateChangingStatus(null)
    }
  }

  const filteredPresets = useMemo(() => {
    if (!pickerSearch) return presets || []
    const q = pickerSearch.toLowerCase()
    return (presets || []).filter(p => p.name.toLowerCase().includes(q))
  }, [presets, pickerSearch])

  // The words under the title. Packaging, and the preset's platform settings,
  // are a SCORM package's: an LTI 1.3 connection has neither.
  const subtitle = isEdit
    ? archived
      ? 'This connection is archived: it accepts no launch, and nothing on this page can change.'
      : !canChange
        ? 'You can view this connection but not change it.'
        : isLtiEdit
          ? 'Enter the tool details in your LMS, then the details it gives you back.'
          : 'Update connection details. Platform and signing inherit from the preset.'
    : isLti
      ? 'Pick your LMS platform to get started. EducationXR then shows you the addresses to enter in your LMS.'
      : 'Pick your LMS platform to get started. EducationXR pre-configures SCORM packaging for each.'

  const hasErrors = Object.keys(fieldErrors).length > 0
  // What the banner says when the service sent no sentence of its own (a proxy's
  // bare 502, a network failure): the connection was not read, or not saved.
  const errorFallback = isEdit && !loaded ? "The connection couldn't be loaded." : "The connection couldn't be saved."
  const homePageMessage = fieldErrors.allowedHomePages || homePageError

  // The status in words, and the one change the Connections list offers.
  const words = connectionStatus(status)
  const statusControl = (
    <div className="lms-status" role="group" aria-label="Status">
      <span className="lms-status-label">Status</span>
      <Pill tone={words.tone}>{words.label}</Pill>
      {statusVerb && canChange && (
        <button
          className="btn sw-btn"
          type="button"
          aria-label={`${statusVerb === 'pause' ? 'Pause' : 'Resume'} ${savedName}`}
          disabled={changingStatus !== null}
          onClick={changeStatus}
          data-testid="connection-status-change"
        >
          {changingStatus === 'pause' ? 'Pausing…' : changingStatus === 'resume' ? 'Resuming…' : statusVerb === 'pause' ? 'Pause' : 'Resume'}
        </button>
      )}
      <span className="visually-hidden" role="status">
        {statusNote}
      </span>
    </div>
  )

  return (
    <FleetPage
      testId="lms-connection-drawer"
      title={isEdit ? name || 'Edit connection' : 'New LMS connection'}
      purpose={<span data-testid="drawer-sub">{subtitle}</span>}
      actions={isEdit && loaded ? statusControl : null}
    >
      <LmsTrail
        trail={lmsTrail(
          { url: CONNECTIONS_PATH, text: 'Connections' },
          isEdit
            ? { url: `${CONNECTIONS_PATH}/${connectionId}`, text: savedName }
            : { url: `${CONNECTIONS_PATH}/new`, text: 'New connection' }
        )}
      />
      <p className="mb-2">
        <Link to={CONNECTIONS_PATH} data-testid="connection-back">
          ← All connections
        </Link>
      </p>
      {/* busy: a save in flight is on its way to the next page, so the guard is off
          for it and back on if the service refuses it. */}
      <LeaveGuard
        when={dirty && !busy}
        message={
          isLtiEdit && lti.dirty
            ? 'The platform details you typed are not saved. Leave this connection without saving your changes?'
            : 'Leave this connection without saving your changes?'
        }
      />
      {statusError && (
        <Notice tone="bad" role="alert">
          {statusError}
        </Notice>
      )}
      <div className="lms-admin lms-in-page">
        {(error || hasErrors) && (
          <div className="error-banner" data-testid="drawer-error-banner">
            <LmsIcon name="circle-exclamation" />
            <div>
              <p className="title">{error ? failureText(error, errorFallback) : "Can't save yet"}</p>
              {hasErrors && <span>Fix the highlighted fields below.</span>}
              {error && error.code === 'CONNECTION_NAME_DUPLICATE' && <span>A connection with this name already exists.</span>}
              {error && error.code === 'SYSTEM_KEY_HAS_LINKS' && (
                <span>Unlink everyone from that source on the Account linking page first, then change the key here.</span>
              )}
            </div>
          </div>
        )}

        {/* Section 1 — platform */}
        <div className="form-section">
          <div className={`form-section-title ${presetID ? 'done' : ''}`}>
            <span className="num">{presetID ? <LmsIcon name="check" style={{ fontSize: 9 }} /> : '1'}</span>
            <h2 className="label">{isEdit ? 'Platform' : 'Pick your platform'}</h2>
            {presetID > 0 && !isEdit && (
              <React.Fragment>
                <span className="spacer" />
                <button type="button" className="btn btn-sm btn-outline-secondary edit-link" onClick={() => updatePresetID(0)}>
                  Change
                </button>
              </React.Fragment>
            )}
          </div>

          {presetID > 0 || isEdit ? (
            <div className="selected-platform" data-testid="selected-platform">
              <div className={`conn-logo ${logoFor(selectedPreset && selectedPreset.name).className}`}>
                {logoFor(selectedPreset && selectedPreset.name).text}
              </div>
              <div className="body">
                <div className="name">{selectedPreset ? selectedPreset.name : `Preset #${presetID || '—'}`}</div>
                <div className="meta">
                  {selectedPreset ? (isLti ? selectedPreset.slug : `${selectedPreset.slug} · ${selectedPreset.scorm_version}`) : 'preset'}
                </div>
              </div>
            </div>
          ) : (
            <div className="platform-picker" data-testid="platform-picker">
              <div className="platform-picker-search">
                <LmsIcon name="magnifying-glass" />
                <input
                  type="text"
                  placeholder="Search platforms…"
                  value={pickerSearch}
                  onChange={e => updatePickerSearch(e.target.value)}
                />
              </div>
              <div className="platform-picker-section">Published presets</div>
              {presets === null && <div className="platform-option">Loading…</div>}
              {filteredPresets.map(preset => {
                const logo = logoFor(preset.name)
                return (
                  <div key={preset.id} className="platform-option" onClick={() => updatePresetID(preset.id)} data-testid={`platform-${preset.id}`}>
                    <div className={`conn-logo ${logo.className}`} style={{ width: 30, height: 30, fontSize: 11 }}>
                      {logo.text}
                    </div>
                    <div className="body">
                      <div className="name">{preset.name}</div>
                      <div className="desc">
                        {preset.description || (isLti ? preset.lms_family || 'LMS' : `${preset.lms_family || 'LMS'} · ${preset.scorm_version}`)}
                      </div>
                    </div>
                    <LmsIcon name="chevron-right" className="chev" />
                  </div>
                )
              })}
              {presets && filteredPresets.length === 0 && (
                <div className="platform-option" style={{ cursor: 'default' }}>
                  <div className="body">
                    <div className="desc">No published presets match — ask your EducationXR contact.</div>
                  </div>
                </div>
              )}
              {fieldErrors.preset && (
                <div className="field-error" style={{ padding: '6px 12px' }}>
                  <LmsIcon name="circle-exclamation" />
                  {fieldErrors.preset}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Section 2 — connection details */}
        <div className="form-section" data-testid="connection-details" inert={blocked} style={blocked ? DIMMED : undefined}>
          <div className="form-section-title">
            <span className="num">2</span>
            <h2 className="label">Connection details</h2>
          </div>
          <div className="field">
            <label className="field-label" htmlFor="connection-name">
              <span className="name">Display name</span>
            </label>
            <input
              id="connection-name"
              type="text"
              className={`form-control form-control-sm ${fieldErrors.name ? 'is-invalid' : ''}`}
              placeholder="e.g. Acme Corporate LMS"
              value={name}
              onChange={e => updateName(e.target.value)}
              disabled={readOnly}
              data-testid="drawer-name"
            />
            {fieldErrors.name ? (
              <div className="field-error">
                <LmsIcon name="circle-exclamation" />
                {fieldErrors.name}
              </div>
            ) : (
              <div className="field-help">How this connection appears in the EducationXR admin and on course pages.</div>
            )}
          </div>
          <div className="field">
            <label className="field-label" htmlFor="connection-base-url">
              <span className="name">LMS base URL</span>
            </label>
            <input
              id="connection-base-url"
              type="text"
              className={`form-control form-control-sm ${fieldErrors.baseURL ? 'is-invalid' : ''}`}
              placeholder="https://lms.acme.com"
              value={baseURL}
              onChange={e => updateBaseURL(e.target.value)}
              disabled={readOnly}
              data-testid="drawer-base-url"
            />
            {fieldErrors.baseURL ? (
              <div className="field-error">
                <LmsIcon name="circle-exclamation" />
                {fieldErrors.baseURL}
              </div>
            ) : (
              <div className="field-help">
                Where your LMS is reachable, e.g. <code>https://lms.acme.com</code>.
                {isLti && ' Learners are recognized by your LMS\'s issuer, below, not by this address.'}
              </div>
            )}
          </div>
          {!isEdit && (
            <div className="field" data-testid="delivery-choice">
              <div className="field-label">
                <span className="name">How courses open</span>
              </div>
              <div className="radio-group" role="radiogroup" aria-label="How courses open">
                {DELIVERY_OPTIONS.map(([key, label, desc]) => (
                  <div
                    key={key}
                    className={`radio-option ${deliveryChoice === key ? 'selected' : ''}`}
                    role="radio"
                    aria-checked={deliveryChoice === key}
                    tabIndex={0}
                    onClick={() => updateDeliveryChoice(key)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        updateDeliveryChoice(key)
                      }
                    }}
                    data-testid={`delivery-${key}`}
                  >
                    <div className="indicator" />
                    <div className="body">
                      <div className="name">{label}</div>
                      <div className="desc">{desc}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="field-help">
                {isLti
                  ? 'After you create it, this page shows the addresses to enter in your LMS, then asks for the details your LMS gives back. It can\'t be changed later.'
                  : 'It can\'t be changed after the connection is made.'}
              </div>
            </div>
          )}
          {isEdit && loaded && (
            <div className="field" data-testid="drawer-delivery">
              <div className="field-label">
                <span className="name">How courses open</span>
              </div>
              <div>
                <span className="pill neutral">{DELIVERY_NAME[delivery || 'scorm'] || delivery}</span>
              </div>
              <div className="field-help">Chosen when the connection was made; it can't be changed.</div>
            </div>
          )}
          {isEdit && scormOnly && (
            <div className="field">
              <div className="field-label">
                <span className="name">Link source key</span>
                <span className="optional">Optional</span>
              </div>
              <input
                type="text"
                className={`form-control form-control-sm ${systemKeyError ? 'is-invalid' : ''}`}
                placeholder="e.g. acme-hr"
                aria-label="Link source key"
                value={systemKey}
                onChange={e => updateSystemKey(e.target.value)}
                disabled={readOnly}
                data-testid="drawer-system-key"
              />
              {systemKeyError ? (
                <div className="field-error" data-testid="drawer-system-key-error">
                  <LmsIcon name="circle-exclamation" />
                  {systemKeyError}
                </div>
              ) : (
                <div className="field-help" data-testid="drawer-system-key-help">
                  Accounts linked through this connection are grouped under this key. It appears in LMS › Account
                  linking. It can't change while the connection has linked learners.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Section 3 — security (a SCORM package's referrer check and cmi5 home pages: none for LTI 1.3) */}
        {scormOnly && (
          <div className="form-section" data-testid="connection-security" inert={blocked} style={blocked ? DIMMED : undefined}>
            <div className="form-section-title">
              <span className="num">3</span>
              <h2 className="label">Security</h2>
            </div>
            <div className="field">
              <div className="field-label">
                <span className="name">Referrer check</span>
              </div>
              <div className="radio-group" role="radiogroup" aria-label="Referrer check">
                {REFERRER_OPTIONS.map(([key, label, desc]) => (
                  <div
                    key={key}
                    className={`radio-option ${referrer === key ? 'selected' : ''}`}
                    role="radio"
                    aria-checked={referrer === key}
                    aria-disabled={readOnly ? 'true' : undefined}
                    tabIndex={0}
                    onClick={() => chooseReferrer(key)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        chooseReferrer(key)
                      }
                    }}
                    data-testid={`referrer-${key}`}
                  >
                    <div className="indicator" />
                    <div className="body">
                      <div className="name">{label}</div>
                      <div className="desc">{desc}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="field-help">
                Each connection signs its packages with its own key{isEdit ? ' (Package key, below)' : ', issued when you create it'}.
              </div>
            </div>
            {!isEdit && <StrictSignInSwitch checked={newStrict} onChange={updateNewStrict} delivery="scorm" />}
            {isCmi5 && (
              <div className="field" data-testid="allowed-home-pages">
                <div className="field-label">
                  <span className="name">Allowed cmi5 home pages</span>
                  <span className="optional">Optional</span>
                </div>
                {allowedHomePages.length === 0 ? (
                  <div className="field-help">None: only this connection's LMS address is accepted.</div>
                ) : (
                  <ul className="list-unstyled mb-0">
                    {allowedHomePages.map(origin => (
                      <li key={origin} data-testid={`allowed-home-page-${origin}`} style={{ padding: '3px 0' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span className="text-mono" style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>
                            {origin}
                          </span>
                          <button
                            className="btn btn-light border btn-sm"
                            type="button"
                            aria-label={`Remove ${origin}`}
                            data-testid={`allowed-home-page-remove-${origin}`}
                            disabled={readOnly}
                            onClick={() => removeHomePage(origin)}
                          >
                            Remove
                          </button>
                        </div>
                        {homePageServerErrors.entries[origin] && (
                          <div className="field-error">
                            <LmsIcon name="circle-exclamation" />
                            {homePageServerErrors.entries[origin]}
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <input
                    type="text"
                    className={`form-control form-control-sm ${homePageMessage ? 'is-invalid' : ''}`}
                    placeholder="https://lms2.acme.com"
                    aria-label="Home page to allow"
                    value={homePageDraft}
                    disabled={readOnly}
                    onChange={e => updateHomePageDraft(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        addHomePage()
                      }
                    }}
                    data-testid="allowed-home-page-input"
                  />
                  <button className="btn btn-outline-secondary btn-sm" type="button" data-testid="allowed-home-page-add" disabled={readOnly} onClick={addHomePage}>
                    Add
                  </button>
                </div>
                {homePageMessage ? (
                  <div className="field-error" data-testid="allowed-home-page-error">
                    <LmsIcon name="circle-exclamation" />
                    {homePageMessage}
                  </div>
                ) : (
                  <div className="field-help">
                    A cmi5 launch is accepted when its home page is this connection's LMS address or one listed here. Add an
                    address only if it belongs to the same LMS.
                  </div>
                )}
                {homePageServerErrors.list && (
                  <div className="field-error" data-testid="allowed-home-pages-server-error">
                    <LmsIcon name="circle-exclamation" />
                    {homePageServerErrors.list}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {isLtiEdit && loaded && (
          <LtiRegistrationSection lti={lti} readOnly={readOnly} unsavedError={fieldErrors.ltiPlatform} />
        )}

        {isEdit && loaded && <LaunchSettings launch={launch} delivery={delivery} readOnly={readOnly} />}

        {isCmi5 && (
          <div className="form-section" data-testid="refused-home-pages">
            <div className="form-section-title">
              <span className="num">
                <LmsIcon name="globe" style={{ fontSize: 9 }} />
              </span>
              <h2 className="label">Refused cmi5 home pages</h2>
            </div>
            <div className="field">
              {diagnosticsError ? (
                <div className="field-help">{readFailureText('The refused home pages', diagnosticsError)}</div>
              ) : refusedHomePages === null ? (
                <div className="field-help">Loading…</div>
              ) : refusedHomePages.length === 0 ? (
                <div className="field-help" data-testid="refused-home-pages-none">
                  No launch was refused for its home page in the last 30 days.
                </div>
              ) : (
                <React.Fragment>
                  <div className="field-help" style={{ marginTop: 0, marginBottom: 6 }}>
                    Launches from these addresses were refused because they are not this connection's LMS. Allow one
                    only if it belongs to the same LMS.
                  </div>
                  <ul className="list-unstyled mb-0">
                    {refusedHomePages.map(entry => (
                      <li
                        key={entry.home_page}
                        data-testid={`refused-home-page-${entry.home_page}`}
                        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}
                      >
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div className="text-mono" style={{ overflowWrap: 'anywhere' }}>
                            {entry.home_page}
                          </div>
                          <div className="field-help" style={{ marginTop: 0 }}>
                            {entry.count} {entry.count === 1 ? 'launch' : 'launches'}
                            {typeof entry.distinct_learners === 'number' &&
                              ` · ${entry.distinct_learners} ${entry.distinct_learners === 1 ? 'learner' : 'learners'}`}
                          </div>
                          <div className="field-help" style={{ marginTop: 0 }} data-testid="refused-home-page-seen">
                            {entry.first_seen_at && (
                              <React.Fragment>
                                {'First refused '}
                                <span title={utcText(entry.first_seen_at)}>{dateTimeText(entry.first_seen_at)}</span>
                                {' · last refused '}
                              </React.Fragment>
                            )}
                            {!entry.first_seen_at && 'Last refused '}
                            <span title={utcText(entry.last_seen_at)}>{dateTimeText(entry.last_seen_at)}</span>
                          </div>
                          {entry.other_connection && (
                            <div
                              className="field-error"
                              style={{ color: 'var(--edxr-status-warning-text)' }}
                              data-testid="refused-home-page-other-connection"
                            >
                              <LmsIcon name="triangle-exclamation" />
                              <span>{otherConnectionWarning(entry.other_connection)}</span>
                            </div>
                          )}
                        </div>
                        {!readOnly && (
                          <button
                            className="btn btn-outline-secondary btn-sm"
                            type="button"
                            disabled={allowing !== null || busy}
                            data-testid={`refused-home-page-allow-${entry.home_page}`}
                            onClick={() => askAllow(entry)}
                          >
                            {allowing === entry.home_page ? 'Allowing…' : 'Allow'}
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                </React.Fragment>
              )}
              {allowError && (
                <div className="field-error" data-testid="refused-home-pages-error">
                  <LmsIcon name="circle-exclamation" />
                  {allowError}
                </div>
              )}
            </div>
          </div>
        )}

        {/* A cmi5 connection's failed read already shows in its refused home pages. */}
        {(isCmi5 || isXapiLrs) && <HeldStatementsSection held={heldStatements} error={isXapiLrs ? diagnosticsError : null} />}

        {(isCmi5 || isXapiLrs) && (
          <FailedStatementsSection connectionId={connectionId} failed={failedStatements} archived={archived} reload={loadDiagnostics} />
        )}

        {isEdit && scormOnly && (
          <PackagesToRebuild connectionId={connectionId} packages={packagesToRebuild} reload={loadDiagnostics} readOnly={readOnly} />
        )}

        {/* A package's key: an LTI 1.3 connection has no package, its tool key is its own. */}
        {isEdit && scormOnly && <SigningKeySection connectionId={connectionId} signingKey={signingKey} onChange={updateSigningKey} readOnly={readOnly} />}

        {isLtiEdit && loaded && lti.registration && (
          <LtiToolKeySection
            connectionId={connectionId}
            toolKey={lti.registration.tool_key}
            onChange={lti.setToolKey}
            readOnly={readOnly}
          />
        )}

        {isLtiEdit && loaded && (
          <LtiDiagnostics
            connectionId={connectionId}
            archived={archived}
            refusals={ltiRefusals}
            scoreFailures={ltiScoreFailures}
            alreadyApplied={ltiScoreApplied409}
            error={diagnosticsError}
          />
        )}

        {/* An LTI 1.3 connection has no package and no system key, and nothing there uses a bound API key. */}
        {isEdit && loaded && !isLti && <ConnectionKeysPanel key={connectionId} connectionId={connectionId} status={status} />}

        {/* Section 4 — display overrides: a section of the page, shown (no expander) */}
        <div
          className="form-section"
          data-testid="display-overrides"
          inert={blocked}
          style={blocked ? DIMMED : undefined}
        >
          <div className="form-section-title">
            <span className="num">
              <LmsIcon name="palette" style={{ fontSize: 9 }} />
            </span>
            <h2 className="label">Display overrides</h2>
          </div>
          <div className="field-help" style={{ marginTop: 0, marginBottom: 12 }}>
            These replace your organization's Branding &amp; Display settings (LMS → Settings) for this connection only. Leave a
            field empty to use the organization's setting.
          </div>
          <div className="field">
            <label className="field-label" htmlFor="display-footer-mode">
              <span className="name">Footer mode</span>
            </label>
            <select
              id="display-footer-mode"
              className="form-select form-select-sm"
              value={overrideFooterMode}
              disabled={readOnly}
              onChange={e => updateOverrideFooterMode(e.target.value)}
            >
              <option value="">Use the organization's setting</option>
              <option value="default">Powered by EducationXR</option>
              <option value="hidden">Hidden (white-label)</option>
              <option value="replace">Your organization's logo (white-label)</option>
            </select>
          </div>
          <div className="field">
            <label className="field-label" htmlFor="display-support-email">
              <span className="name">Support email</span>
            </label>
            <input
              id="display-support-email"
              className="form-control form-control-sm"
              type="email"
              value={overrideSupportEmail}
              disabled={readOnly}
              onChange={e => updateOverrideSupportEmail(e.target.value)}
            />
          </div>
          <div className="field">
            <label className="field-label" htmlFor="display-support-link">
              <span className="name">Support link</span>
            </label>
            <input
              id="display-support-link"
              className="form-control form-control-sm"
              type="url"
              value={overrideSupportURL}
              disabled={readOnly}
              onChange={e => updateOverrideSupportURL(e.target.value)}
            />
          </div>
        </div>

        {/* Save and Discard: the admin's docked save bar (owner 2026-10-23: they
            floated over the form as a card), up while something is unsaved. The
            back link and the trail leave the page; the leave guard asks first. */}
        {!readOnly && (
          <DockedSaveBar
            testId="connection-savebar"
            saveTestId="drawer-save"
            discardTestId="drawer-discard"
            on={dirty}
            count={unsavedCount}
            error={dirty && (error || hasErrors) ? (error ? failureText(error, errorFallback) : "Can't save yet: fix the highlighted fields.") : null}
            failure={error}
            busy={busy}
            canSave={allowing === null && (isEdit || presetID > 0)}
            onSave={handleSave}
            onDiscard={discard}
            saveLabel={isEdit ? 'Save changes' : 'Create connection'}
            busyLabel={isEdit ? 'Saving…' : 'Creating…'}
          />
        )}
        {askingOrigin && (
          <div className="lms-modal-scrim" data-testid="issuer-change-modal">
            <div className="lms-modal" role="dialog" aria-modal="true" aria-label="Is this the same LMS?">
              <div className="modal-head">
                <div className="icon">
                  <LmsIcon name="globe" />
                </div>
                <div className="body">
                  <h2>Is this the same LMS?</h2>
                  <p className="target">
                    <span className="text-mono">{savedBaseURL}</span> →{' '}
                    <span className="text-mono">{baseURL.trim()}</span>
                  </p>
                </div>
                <button
                  className="close-btn"
                  title="Close"
                  type="button"
                  onClick={() => updateAskingOrigin(false)}
                >
                  <LmsIcon name="xmark" />
                </button>
              </div>
              <div className="lms-modal-body">
                <p className="field-help" style={{ marginTop: 0 }}>
                  Learners are recognized by the id their LMS sends, within that LMS. Tell us which this is, so nobody is
                  linked to the wrong account.
                </p>
                <button
                  className="btn btn-outline-secondary w-100 text-start mb-2"
                  type="button"
                  data-testid="issuer-change-moved"
                  onClick={() => answerOrigin('moved')}
                >
                  <strong>Same LMS, new address — learners stay linked</strong>
                  <span className="d-block field-help">
                    Their links move to the new address. If another connection still uses the old address, links stay
                    where they are, and learners who launch through this one link their accounts again.
                  </span>
                </button>
                <button
                  className="btn btn-outline-danger w-100 text-start"
                  type="button"
                  data-testid="issuer-change-new-lms"
                  onClick={() => answerOrigin('new_lms')}
                >
                  <strong>A different LMS — learners link again</strong>
                  <span className="d-block field-help">
                    Existing links stay with the old LMS; learners from this one link their accounts again on their next
                    launch. This connection's packages stop working now, in the old LMS too: build new packages and upload
                    them to the new LMS.
                  </span>
                </button>
              </div>
              <div className="modal-foot">
                <span className="help" />
                <button
                  className="btn btn-light border btn-sm"
                  type="button"
                  data-testid="issuer-change-cancel"
                  onClick={() => updateAskingOrigin(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
        {confirmingAllow && (
          <div className="lms-modal-scrim" data-testid="allow-home-page-modal">
            <div className="lms-modal" role="dialog" aria-modal="true" aria-label="Allow another connection's address?" ref={allowDialog}>
              <div className="modal-head">
                <div className="icon">
                  <LmsIcon name="triangle-exclamation" />
                </div>
                <div className="body">
                  <h2>Allow another connection's address?</h2>
                  <p className="target">
                    <span className="text-mono">{confirmingAllow.entry.home_page}</span>
                  </p>
                </div>
                <button className="close-btn" title="Close" type="button" onClick={() => updateConfirmingAllow(null)}>
                  <LmsIcon name="xmark" />
                </button>
              </div>
              <div className="lms-modal-body">
                <p className="field-help" style={{ marginTop: 0 }}>
                  {otherConnectionWarning(confirmingAllow.entry.other_connection)} Allow it only if both connections are the same
                  LMS.
                </p>
              </div>
              <div className="modal-foot">
                <span className="help" />
                <button
                  className="btn btn-light border btn-sm"
                  type="button"
                  ref={allowCancel}
                  data-testid="allow-home-page-cancel"
                  onClick={() => updateConfirmingAllow(null)}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-danger btn-sm"
                  type="button"
                  disabled={busy || allowing !== null}
                  data-testid="allow-home-page-confirm"
                  onClick={confirmAllow}
                >
                  Allow
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </FleetPage>
  )
}

export const ConnectionDrawerNew = () => <ConnectionDrawer mode="new" />
export const ConnectionDrawerEdit = () => <ConnectionDrawer mode="edit" />
